import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LogoWordmark } from "../components/Logo";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const next = searchParams.get("next") || "/dashboard";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            // Sicurezza: accetta solo path interni (no URL esterne)
            const safeNext = next.startsWith("/") && !next.startsWith("//") ? next : "/dashboard";
            navigate(safeNext);
        } else setError(res.error);
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            {/* Left: form */}
            <div className="flex flex-col p-6 sm:p-10 lg:p-16 grid-bg">
                <LogoWordmark />
                <div className="flex-1 flex items-center">
                    <div className="w-full max-w-md fade-up">
                        <div className="inline-flex items-center gap-2 bg-[#C7FF2C]/20 text-[#0C4650] px-3 py-1 rounded-full text-xs font-bold mb-4">
                            <Zap className="w-3 h-3" /> AREA AGENTI
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0C4650] leading-tight">
                            Bentornato.
                        </h1>
                        <p className="mt-3 text-gray-600 text-lg">
                            Accedi per creare nuovi preventivi e gestire le tue richieste.
                        </p>

                        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
                            <div>
                                <label className="label">Email</label>
                                <input
                                    type="email"
                                    className="field"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="login-email"
                                    placeholder="nome.cognome@azienda.com"
                                />
                            </div>
                            <div>
                                <label className="label">Password</label>
                                <input
                                    type="password"
                                    className="field"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="login-password"
                                    placeholder="••••••••"
                                />
                                <div className="text-right mt-1.5">
                                    <Link
                                        to="/forgot-password"
                                        className="text-xs font-bold text-[#0C4650] hover:text-[#091f26] underline underline-offset-2"
                                        data-testid="go-forgot-password"
                                    >
                                        Password dimenticata?
                                    </Link>
                                </div>
                            </div>
                            {error && (
                                <div
                                    className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 text-sm"
                                    data-testid="login-error"
                                >
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full"
                                data-testid="login-submit"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                Accedi
                            </button>
                        </form>

                        <div className="mt-8 text-sm text-gray-600">
                            Non hai ancora un profilo?{" "}
                            <Link
                                to="/register"
                                className="font-bold text-[#0C4650] underline underline-offset-4 hover:text-[#091f26]"
                                data-testid="go-register"
                            >
                                Richiedi l'abilitazione
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="text-xs text-gray-400 mt-8">
                    © {new Date().getFullYear()} Scintilla Hub — Accendiamo connessioni.
                </div>
            </div>

            {/* Right: hero */}
            <div
                className="hidden lg:block relative bg-[#0C4650] overflow-hidden"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1600&q=80')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-[#0C4650]/95 via-[#0C4650]/80 to-[#0C4650]/60" />
                <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
                    <div />
                    <div>
                        <div className="text-[11px] tracking-[0.3em] font-bold text-[#C7FF2C] mb-4">
                            PIATTAFORMA PREVENTIVI
                        </div>
                        <h2 className="text-5xl font-black leading-tight">
                            Efficienza Energetica
                            <br />
                            <span className="text-[#C7FF2C]">&amp;</span> Noleggio Lungo Termine
                        </h2>
                        <p className="mt-6 text-white/80 text-lg max-w-md">
                            Un unico strumento per costruire proposte commerciali con il supporto
                            diretto della sede.
                        </p>
                        <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
                            {[
                                { n: "30′", l: "Risposta media" },
                                { n: "∞", l: "Offerte attive" },
                            ].map((s) => (
                                <div
                                    key={s.l}
                                    className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4"
                                >
                                    <div className="text-3xl font-black text-[#C7FF2C]">{s.n}</div>
                                    <div className="text-xs uppercase tracking-wider text-white/60 mt-1">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
